<template>
  <div class="housing">
    <input-label :text="$t('housing.type_logement.label')"/>
    <input-selector v-model="housingTypeSync" :options="housingType" button-class="housing-button">
      <template v-slot:default="slotProps">
        <div class="housing-type">
          <div class="housing-type-icon" :class="slotProps.option.css"/>
          <span class="housing-type-label">{{slotProps.option.label}}</span>
        </div>
      </template>
    </input-selector>
    <next-transition
      :visible="showCaveQuestion">
        <input-label :text="$t('housing.cave.label')"/>
        <input-selector v-model="caveSync" :options="caveOptions" button-class="cave-button"/>
    </next-transition>
    <next-transition
      :visible="showFloorQuestion">
      <input-label :text="$t(`housing.floor.label.${this.parentRoute}`)" />
      <input-selector
        data-cy="floor-option"
        v-model="floorSync"
        :options="floorOptions"
        :multiple="true"
        button-class="floor-input" />
    </next-transition>
  </div>
</template>

<script>
import InputSelector from '@/components/utils/InputSelector.vue';
import InputLabel from '@/components/utils/InputLabel.vue';
import NextTransition from '@/components/utils/NextTransition.vue';
import { isSpecialLogementType, isUnknownLogementType } from '@/store/utils';

export default {
  name: 'housing',
  components: {
    NextTransition,
    InputLabel,
    InputSelector,
  },
  computed: {
    housingTypeSync: {
      get() {
        return this.$store.getters[`${this.parentRoute}Store/floor`]?.type_logement;
      },
      set(type_logement) {
        this.$store.commit(`${this.parentRoute}Store/SET_TYPES_LOGEMENT`, type_logement);
      },
    },
    caveSync: {
      get() {
        return this.$store.getters[`${this.parentRoute}Store/floor`]?.cave;
      },
      set(cave) {
        this.$store.commit(`${this.parentRoute}Store/SET_CAVE`, cave);
      },
    },
    floorSync: {
      get() {
        const { floor } = this.$store.getters[`${this.parentRoute}Store/floor`];
        return floor ? floor.split('+') : [];
      },
      set(floor) {
        this.$store.commit(`${this.parentRoute}Store/SET_FLOOR`, floor.join('+'));
      },
    },
    showCaveQuestion() {
      return this.housingTypeSync !== null
        && !isUnknownLogementType(this.housingTypeSync)
        && !isSpecialLogementType(this.housingTypeSync);
    },
    showFloorQuestion() {
      return this.showCaveQuestion
        && this.caveSync !== null;
    },
    housingType() {
      return [{
        value: 'DGTS_LOG_APPART',
        label: this.$t('housing.type_logement.options.apartment'),
        css: 'flat',
      },
      {
        value: 'DGTS_LOG_MAISON',
        label: this.$t('housing.type_logement.options.house'),
        css: 'house',
      },
      {
        value: 'DGTS_LOG_DUPLEX',
        label: this.$t('housing.type_logement.options.duplex'),
        css: 'duplex',
      },
      {
        value: 'DGTS_LOG_TRIPLEX',
        label: this.$t('housing.type_logement.options.triplex'),
        css: 'triplex',
      },
      {
        value: 'DGTS_LOG_GARDEMEUBLE',
        label: this.$t('housing.type_logement.options.garde_meuble'),
        css: 'garde_meuble',
      },
      {
        value: 'DGTS_LOG_OTHER',
        label: this.$t('housing.type_logement.options.other'),
        css: 'other',
      }];
    },
    caveOptions() {
      return [
        {
          value: 1,
          label: this.$t('base.yes'),
        },
        {
          value: -1,
          label: this.$t('housing.cave.options.yesBut'),
        },
        {
          value: 0,
          label: this.$t('base.no'),
        },
      ];
    },
    floorOptions() {
      return [
        {
          value: '-1',
          label: '-1',
        },
        {
          value: '0',
          label: this.$t('housing.floor.options.groundFloor'),
        },
        ...Array.from(
          Array(10), (_, x) => (
            { value: `${x + 1}`, label: `${x + 1}` }
          ),
        ),
        {
          value: '>10',
          label: this.$t('housing.floor.options.superiorTen'),
        },
      ];
    },
    parentRoute() {
      return this.$route.path.split('/')[1];
    },
  },
};
</script>

<style scoped lang="scss">
  ::v-deep{
    @include breakpoint(tablet){
      .housing-button{
        flex-basis: 22%;
      }
    }
    .cave-button{
      min-width: 85px;
    }
    .floor-input {
      flex: initial;
      width: 60px;
    }
  }

  .housing-type-label{
    padding: .3em;
    font-size: 14px;
    font-weight: $--font-weight-500;
  }

  .housing-type {
    display: grid;
    grid-template-columns: auto;
    justify-content: center;
    justify-items: center;

    &-icon {
      height: 88px;
      width: 88px;
    }
  }

  .flat{
    background-image: url('~@/assets/img/flat.png');
  }
  .house{
    background-image: url('~@/assets/img/house.png');
  }
  .triplex{
    background-image: url('~@/assets/img/triplex.png');
  }
  .duplex{
    background-image: url('~@/assets/img/duplex.png');
  }
  .garde_meuble {
    background-image: url('~@/assets/img/garde_meuble.png');
  }
  .other {
    background-image: url('~@/assets/img/other.png');
  }
</style>
